/**
 * @module labels
 */
const { getMagnoliaItem } = require('./getMagnoliaItem');

/**
 * Convenience function to get the list of labels from the Magnolia API.
 *
 * @param {object} params - The function params object.
 * @param {string} params.ancestor - The ancestor value for the Magnolia API call.
 * @param {MgnlEnvVars} params.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 * @param {object} [params.workspaceMap] - Data object map of paths and workspace values. If not provided, the default values found in the MAGNOLIA_ENDPOINT_WORKSPACE_MAP constant will be used.
 *
 * @returns {Array|object} The list of label objects.
 */
async function getLabelList({ ancestor, mgnlEnvVars, workspaceMap }) {
  const envVars = mgnlEnvVars || process.env;
  const data = await getMagnoliaItem({
    caller: 'src/helpers/labels/getLabelList',
    mgnlEnvVars: envVars,
    path: `/.rest/delivery/labeldictionary?@ancestor=/${ancestor}`,
    workspaceMap,
  }); // NOSONAR
  return data.results;
}

/**
 * Convenience function to find and return the label value for the specified label name.
 *
 * @param {Array} labels - Array of labels objects.
 * @param {string} name - The name of the label to find in the specified array of labels.
 *
 * @returns {string} The label value, or a blank string if not found.
 */
function getLabelValue(labels, name) {
  const obj = labels ? labels.find((item) => item.name === name) : '';
  return obj ? obj.value : '';
}

/**
 * Convenience function to find and return the label value for the specified label name.
 *
 * Note: This function is updated of getLabelValue to support the media migration, since to fetch labels we are
 * still using mediaV1 types naming we need to get the corresponding media type.
 *
 * @param {Array} labels - Array of labels objects.
 * @param {string} name - The name of the label to find in the specified array of labels.
 *
 * @returns {string} The label value, or a blank string if not found.
 */
function getLabelValueV2(labels, name) {
  const mediaTypes = {
    'early-childhood': 'early_childhood',
    lifegroups: 'life_groups',
    messages: 'weekend_message',
    'weekend-messages': 'weekend_message',
  };
  const mediaElementName = mediaTypes[name] || name;

  if (mediaElementName && labels && labels.length) {
    const obj = labels.find((item) => item.name === mediaElementName);
    if (!obj || !obj.value) {
      return '';
    }
    return obj.value;
  }

  return '';
}

module.exports = {
  getLabelList,
  getLabelValue,
  getLabelValueV2,
};
